<template>
    <div
        class="drs-tariff"
        :class="{
            'is-selected': isSelected,
            'is-featured': isFeatured,
            'is-disabled': isDisabled
        }"
        v-tooltip="isDisabled ? tariff.availability_description || 'Opcja niedostępna' : ''"
    >
        <radio-input
            class="font-weight-bold"
            :data="{
                label: tariff.name,
                value: true,
            }"
            :value="isSelected"
            :disabled="isDisabled"
            @click.native="selectTariff(tariff)"
        />

        <p class="mb-0 text-sm mt-1 ml-4" v-text="description" />

        <div class="d-flex flex-column w-100 mt-4">
            <h5 class="drs-tariff__desc-title mb-0" v-text="'Pakiet podstawowy:'" />

            <template v-if="isDiscounted">
                <div class="drs-tariff__row mt-1 gap-1">
                    <span class="text-sm">
                        Do {{ discountedPriceEndDate }}
                    </span>
                    <span class="font-weight-bold">
                        {{ tariff.discounted_price | currency }} / mies.
                    </span>
                </div>
                <div class="drs-tariff__row text-orange">
                    <span class="text-sm">
                        Od {{ regularPriceStartDate }}
                    </span>
                    <span class="font-weight-bold">
                        {{ tariff.price | currency }} / mies.
                    </span>
                </div>
                <div v-if="areExtremesValid" class="drs-tariff__savings text-right font-weight-bold">
                    <template v-if="tariffSavings > 0">
                        oszczędzasz {{ tariffSavings }}%*
                    </template>
                </div>
            </template>
            <template v-else>
                {{ tariff.price | currency }} / mies.
            </template>
        </div>

        <hr>

        <div class="d-flex flex-column w-100 mt-4">
            <h6 class="drs-tariff__desc-title mb-0" v-text="'Dodatkowe urządzenia:'" />
            <template v-if="isDiscounted">
                <div class="drs-tariff__row mt-1 gap-1">
                    <span class="text-sm">
                        Do {{ discountedPriceEndDate }}
                    </span>
                    <span class="font-weight-bold">
                        {{ tariff.devices_discounted_price | currency }} / mies.
                    </span>
                </div>
                <div class="drs-tariff__row text-orange">
                    <span class="text-sm">
                        Od {{ regularPriceStartDate }}
                    </span>
                    <span class="font-weight-bold">
                        {{ tariff.devices_price | currency }} / mies.
                    </span>
                </div>
                <div v-if="areExtremesValid" class="drs-tariff__savings text-right font-weight-bold">
                    <template v-if="devicesSavings > 0">
                        oszczędzasz {{ devicesSavings }}%*
                    </template>
                </div>
            </template>
            <template v-else>
                {{ tariff.devices_price | currency }} / mies.
            </template>
        </div>
    </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { TIMELINES_NOUNS, TIMELINES } from '@/utils/constants/drs/tariffs'

export default {
    TIMELINES,

    TIMELINES_NOUNS,

    props: {
        tariff: {
            type: Object,
            required: true
        },

        extremes: {
            type: Object,
            default: () => ({})
        }
    },

    methods: {
        ...mapActions('drsTariffs', ['selectTariff'])
    },

    computed: {
        ...mapGetters('drsTariffs', ['selectedTariff']),

        isDiscounted() {
            return this.tariff.discounted_price && this.tariff.discounted_price < this.tariff.price
        },

        discountedPriceEndDate() {
            return this.$moment(this.tariff.discounted_price_date_to).subtract(1, 'day').format('DD.MM.YYYY')
        },

        regularPriceStartDate() {
            return this.$moment(this.tariff.discounted_price_date_to).format('DD.MM.YYYY')
        },

        isSelected() {
            return this.selectedTariff?.id === this.tariff.id
        },

        isFeatured() {
            return !!this.areExtremesValid && this.extremes.cheapest.id === this.tariff.id
        },

        isDisabled() {
            return this.tariff.available === false
        },

        description() {
            if (this.tariff.timeline === TIMELINES.MONTHLY) {
                return 'Płatność co miesiąc'
            }

            if (this.tariff.timeline === TIMELINES.YEARLY) {
                return 'Płatność za 12 miesięcy z góry'
            }

            return ''
        },

        areExtremesValid() {
            return this.extremes.cheapest && this.extremes.mostExpensive
        },

        tariffSavings() {
            if (!this.areExtremesValid) {
                return 0
            }

            const mostExpensivePrice = this.extremes.mostExpensive.annualPrice
            return Math.round(((mostExpensivePrice - this.tariff.annualPrice) / mostExpensivePrice) * 100)
        },

        devicesSavings() {
            if (!this.areExtremesValid) {
                return 0
            }

            const mostExpensiveDevicesPrice = this.extremes.mostExpensive.annualPriceDevices

            return Math.round(
                ((mostExpensiveDevicesPrice - this.tariff.annualPriceDevices) / mostExpensiveDevicesPrice) * 100
            )
        }
    }
}
</script>

<style lang="scss">
.drs-tariff {
    --border-width: 2px;
    padding: 1.5rem 1rem;
    border: var(--border-width) solid var(--color-dark);
    border-radius: 4px;
    color: var(--color-primary);

    &__row {
        display: flex;
        justify-content: space-between;
        align-items: center;
    }

    &__savings {
        height: 24px;
    }

    &.is-disabled {
        color: var(--text-gray);
        .drs-tariff__desc-title {
            color: var(--text-gray);
        }
    }

    &:not(.is-disabled) {

        .drs-tariff__desc {
            color: var(--color-primary);
        }

        .drs-tariff__warn {
            color: var(--color-orange);
        }
    }

    &.is-selected {
        background-color: var(--active-promo-bg);
        color: var(--color-primary);
        border-color: var(--color-dark);
    }

    &.is-featured {
        position: relative;

        &:before {
            --el-height: 2rem;
            content: 'POLECAMY';
            position: absolute;
            display: flex;
            align-items: center;
            justify-content: center;
            top: calc(var(--el-height) * -1);
            text-align: center;
            left: calc(var(--border-width) * -1);
            right: calc(var(--border-width) * -1);
            font-size: 14px;
            background-color: var(--color-primary);
            color: var(--color-white);
            border-top-left-radius: 4px;
            border-top-right-radius: 4px;
            height: var(--el-height);
        }
    }
}
</style>